import * as React from "react";
import { History, Location } from "history";
import { connect, Dispatch } from "react-redux";
import { RouteComponentProps } from "react-router";
import { UsersPageUrl } from "../common/urls";
import { DefaultLayout } from "../layouts";
import { AppState, UserAccountInfo, removeUserByIdAction, isReportingOnlyCustomer } from "@store";
import "./Users.css";
import { FetchingState, FETCH_STATE_SUCCESS } from "rd-redux-http";
import { UsersTableComponent, CommonSearchComponent, ModalWindow, CreateUser } from "@components";

export interface UsersComponentProps {
    history: History;
    location: Location;
    searchValue: string;
    loadingFailed: boolean;
    loading: boolean;
    clientId: string;
    selectedPage: number;
    loadingUsers: boolean;
    users: UserAccountInfo[];
    createUserModalStatus: boolean;
    disableContent: boolean;
    isProcessing: boolean;
    onSearch: (query: string) => void;
    onPageChange: (page: number) => void;
    onSort: (sortUrl: string) => void;
    onDelete: (userId: string) => void;
}

export class UsersComponent extends React.Component<UsersComponentProps> {
    renderAside(): React.ReactChild {
        return <div />;
    }
    render() {
        const {
            disableContent,
            loadingFailed,
            history,
            location,
            clientId,
            loading,
            loadingUsers,
            users,
            selectedPage,
            searchValue,
            onSearch,
            onSort,
            onDelete,
            onPageChange,
            createUserModalStatus,
            isProcessing
        } = this.props;

        return (
            <DefaultLayout
                history={history}
                location={location}
                asideChildren={() => this.renderAside()}
                selectedPage="users"
                clientId={clientId}
                loading={loading || isProcessing}
                loadingFailed={loadingFailed}
                disableContent={disableContent}
            >
                <section className="users__header">
                    <h2 className="users__header-title">User Management</h2>
                </section>
                <section className="users__filters">
                    <CommonSearchComponent placeholder="Search User" searchValue={searchValue} onSubmit={onSearch} />
                </section>
                <section className="users__table">
                    <UsersTableComponent
                        users={users}
                        loadingUsers={loadingUsers}
                        items={10}
                        selectedIndex={selectedPage}
                        onPageChange={onPageChange}
                        onDelete={(userId: string) => onDelete(userId)}
                        onSort={onSort}
                    />
                </section>
                <ModalWindow modalId="createUserPopup" state={createUserModalStatus} transition={true}>
                    <CreateUser />
                </ModalWindow>
            </DefaultLayout>
        );
    }
}

export interface UsersPageProps extends RouteComponentProps<{}> {}

export const UsersPage = connect(
    (state: AppState, ownProps: UsersPageProps): Partial<UsersComponentProps> => {
        const match = UsersPageUrl.match(ownProps.location, true);
        const matchUsersUrl = UsersPageUrl.match(ownProps.history.location.pathname, true);
        const onlyReporting = isReportingOnlyCustomer(state);

        let users: any;
        let searchValue: string;
        let sortBy: string;
        let page: number = 0;
        let pageItems: number = 10;

        if (match.isMatched && match.query) {
            searchValue = match.query.search;
            page = Number(match.query.page ? match.query.page : 0);
            sortBy = match.query.sortBy;
        }

        searchValue = searchValue ? searchValue : "";

        if (state.usersState.usersList.fetchState === FETCH_STATE_SUCCESS) {
            users = state.usersState.usersList.data;
        }

        if (users && searchValue) {
            users = users.filter((user: UserAccountInfo) => {
                const regExp = new RegExp(searchValue.trim().toLowerCase());
                const userName = user.email && user.email.trim().toLocaleLowerCase();

                return regExp.test(userName);
            });
        }

        if (users && sortBy) {
            const sortByValue = sortBy.replace(/ [A-Za-z]*/, "");
            const sortType = sortBy.replace(/[A-Za-z]* /, "");

            users = users.sort((userA: UserAccountInfo, userB: UserAccountInfo) => {
                const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
                const a = String(userA[sortByValue]).toLocaleLowerCase();
                const b = String(userB[sortByValue]).toLocaleLowerCase();

                if (String(userA[sortByValue]).toLocaleLowerCase() !== [a, b].sort(collator.compare)[0])
                    return sortType === "asc" ? -1 : 1;
                return sortType === "asc" ? 1 : -1;
            });
        }

        if (users && page - 1 > users.length / pageItems) {
            // If page index is highter than pages amount,reset page index to 0
            page = 0;
        }

        return {
            history: ownProps.history,
            location: ownProps.history.location,
            clientId: matchUsersUrl.isMatched && matchUsersUrl.params["clientId"],
            loading: state.clientState.clientSavingFlag,
            loadingUsers: FetchingState.isFetching(state.usersState.usersList),
            searchValue,
            users,
            selectedPage: page != 0 ? page - 1 : page,
            createUserModalStatus: state.usersState.createUserModalStatus,
            disableContent: onlyReporting,
            isProcessing: state.campaignState.isSaveOrPurchaseProcessing
        };
    },
    (dispatch: Dispatch, ownProps: UsersPageProps) => ({
        onSearch: (query: string) => {
            const match = UsersPageUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const newUrl = UsersPageUrl.format(match.params, {
                    ...match.query,
                    search: query
                });
                ownProps.history.push(newUrl);
            }
        },

        onPageChange: (page: number) => {
            const match = UsersPageUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const query = { ...match.query, ...{ page } };
                const newUrl = UsersPageUrl.format(match.params, query);
                ownProps.history.push(newUrl);
            }
        },

        onSort: (sortUrl: string) => {
            const match = UsersPageUrl.match(ownProps.location, true);
            if (match.isMatched) {
                const newUrl = UsersPageUrl.format(match.params, {
                    ...match.query,
                    sortBy: sortUrl
                });
                ownProps.history.push(newUrl);
            }
        },
        onDelete: (userId: string) => {
            let state = confirm("Are you sure wan't to delete user?");
            if (state) {
                dispatch(removeUserByIdAction({ userId }));
            }
        }
    })
)(UsersComponent);
